import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import api from "../../api";

export default function PairResults() {
  const [event, setEvent] = useState(null);
  const [results, setResults] = useState([]);
  const [archiveRows, setArchiveRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);
  const [toast, setToast] = useState("");
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 4000); };
  const prizesRef = React.useRef({ nearest_pin: "", nearest_pin2: "", longest_drive: "" });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const eventRes = await api.get("/events/current");
      if (!eventRes.data) { setLoading(false); return; }
      setEvent(eventRes.data);

      const [scoresRes, groupsRes, holesRes, prizesRes] = await Promise.all([
        api.get("/scores/" + eventRes.data.id),
        api.get("/groups"),
        api.get("/courses/" + eventRes.data.course_id + "/holes"),
        api.get("/events/prizes").catch(() => ({ data: { nearest_pin: "", nearest_pin2: "", longest_drive: "" } }))
      ]);

      const players = (groupsRes.data.players || []).filter(p => p.playing);
      const holes = holesRes.data;
      const scores = scoresRes.data;
      const isMedal = eventRes.data.game_type?.includes("Medal") || eventRes.data.game_type?.includes("Stroke");

      const grpMap = {};
      players.forEach(p => {
        if (!grpMap[p.grp]) grpMap[p.grp] = {};
        const tid = p.team_id || 0;
        if (!grpMap[p.grp][tid]) grpMap[p.grp][tid] = [];
        grpMap[p.grp][tid].push(p);
      });

      const pairResults = [];
      const wizardHolePoints = [];
      const archiveList = [];
      Object.entries(grpMap).sort((a,b) => parseInt(a[0])-parseInt(b[0])).forEach(([grp, teamGroups]) => {
        Object.entries(teamGroups).forEach(([tid, members]) => {
          const combHcp = members.reduce((s,p) => s + ((p.hcp_adj ?? p.handicap)||0), 0);
          // Foursomes: 50% of combined Course Handicap (hcp_adj). Greensomes:
          // 60% of the lower + 40% of the higher — matches the score entry
          // screen's formula exactly, per Rules of Handicapping.
          const isGreensomes = (eventRes.data.game_type || "").includes("Greensomes");
          const lowerHcp = Math.min(...members.map(p => (p.hcp_adj ?? p.handicap)||0));
          const higherHcp = Math.max(...members.map(p => (p.hcp_adj ?? p.handicap)||0));
          const allowance = isGreensomes
            ? Math.round(0.6 * lowerHcp + 0.4 * higherHcp)
            : Math.round(combHcp * 0.5);
          const leadPlayer = members[0];
          const pairScores = scores.filter(s => s.player_id === leadPlayer.player_id);
          const names = members.map(p => p.name).join(" / ");
          const unmatched = members.length !== 2;

          let gross = 0, points = 0;
          const holeGross = [];
          holes.forEach(hole => {
            const s = pairScores.find(s => s.hole === hole.hole);
            if (s) {
              const g = parseInt(s.gross);
              gross += g;
              const base = Math.floor(allowance/18);
              const extra = allowance%18;
              const shots = base + (hole.stroke_index <= extra ? 1 : 0);
              const net = g - shots;
              const pts = Math.max(0, 2 + (hole.par - net));
              points += pts;
              wizardHolePoints.push({ grp: parseInt(grp), tid, hole: hole.hole, pts, net });
              holeGross.push(g);
            } else { holeGross.push(0); }
          });

          const net = gross - allowance;
          pairResults.push({ grp: parseInt(grp), tid, names, combHcp, allowance, gross: gross||"-", net: gross ? net : "-", points: points||"-", thru: pairScores.length, unmatched });

          // Alternate-shot pairs share one ball and one card — both partners
          // are credited with the same shared gross/net/points/holes, same
          // convention as Better Ball's shared-pair archiving.
          members.forEach(p => {
            archiveList.push({ player_id: p.player_id, name: p.name, handicap: p.handicap, grpTid: grp + "_" + tid, gross: gross || 0, net: gross ? net : 0, points: points || 0, holeGross: [...holeGross] });
          });
        });
      });

      pairResults.sort((a,b) => {
        if (isMedal) return (a.net==="-"?9999:a.net) - (b.net==="-"?9999:b.net);
        return (b.points==="-"?-1:b.points) - (a.points==="-"?-1:a.points);
      });

      pairResults.forEach((p,i) => { p.pos = i+1; });
      setResults(pairResults);
      window.__wizardResult = pairResults;
      window.__wizardHolePoints = wizardHolePoints;

      const posByGrpTid = {};
      pairResults.forEach(p => { posByGrpTid[p.grp + "_" + p.tid] = p.pos; });
      archiveList.forEach(r => { r.pos = posByGrpTid[r.grpTid] || 0; });
      setArchiveRows(archiveList);

      prizesRef.current = {
        nearest_pin: prizesRes.data.nearest_pin || "",
        nearest_pin2: prizesRes.data.nearest_pin2 || "",
        longest_drive: prizesRes.data.longest_drive || "",
      };
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const archiveResults = async () => {
    if (!event || archiveRows.length === 0) return;
    if (!window.confirm("Archive these Pair results? This locks in the round permanently.")) return;
    setArchiving(true);
    try {
      const prizes = prizesRef.current;
      const archiveData = archiveRows.map(p => ({
        position: p.pos, rank: p.pos, player_name: p.name, handicap: p.handicap,
        gross: p.gross, net: p.net, points: p.points,
        holes: (p.holeGross || []).map((g, i) => ({ hole: i + 1, gross: g })).filter(hh => hh.gross > 0),
      }));
      const twos = [];
      archiveRows.forEach(p => { (p.holeGross || []).forEach((g, i) => { if (g === 2) twos.push({ player_name: p.name, hole: i + 1 }); }); });
      const res = await api.post("/archive/save", { event_id: event.id, results: archiveData, ...prizes, twos });
      showToast("Results archived!");
      const changes = res.data?.handicapChanges;
      if (changes && changes.length) {
        const lines = changes.map(c => `${c.name}: ${c.from} → ${c.to}`).join("\n");
        window.alert("Handicaps adjusted:\n\n" + lines);
      }
    } catch(e) { showToast("Failed: " + (e.response?.data?.error || e.message)); }
    finally { setArchiving(false); }
  };

  const isMedal = event?.game_type?.includes("Medal") || event?.game_type?.includes("Stroke");

  return (
    <Layout>
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">Pair Results</span>
          {event && <span className="text-xs text-blue-600 font-medium">{event.game_type}</span>}
          <button onClick={archiveResults} disabled={archiving || archiveRows.length === 0}
            className="ml-auto bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white text-xs font-semibold px-4 py-1.5 rounded">
            {archiving ? "Archiving..." : "Archive Results"}
          </button>
        </div>
        {toast && <div className="bg-blue-50 text-blue-700 px-4 py-2 text-sm border-b border-blue-200">{toast}</div>}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-green-800 text-white">
              <tr>
                <th className="px-4 py-2 text-center w-12">Pos</th>
                <th className="px-4 py-2 text-left">Pair</th>
                <th className="px-4 py-2 text-center w-20">Comb HCP</th>
                <th className="px-4 py-2 text-center w-20">Allowance</th>
                <th className="px-4 py-2 text-center w-16">Thru</th>
                <th className="px-4 py-2 text-center w-16">Gross</th>
                <th className="px-4 py-2 text-center w-16">Net</th>
                {!isMedal && <th className="px-4 py-2 text-center w-16">Pts</th>}
              </tr>
            </thead>
            <tbody>
              {results.map((r,i) => (
                <tr key={r.grp + "_" + r.tid} className={"border-b border-gray-200 " + (i%2===0?"bg-white":"bg-green-50")}>
                  <td className="px-4 py-2 text-center font-bold">{r.pos}</td>
                  <td className="px-4 py-2">
                    {r.unmatched && <span title="This group isn't split 2-and-2 by team — check team assignment on the Players page" className="mr-1">⚠️</span>}
                    {r.names}
                  </td>
                  <td className="px-4 py-2 text-center">{r.combHcp}</td>
                  <td className="px-4 py-2 text-center">{r.allowance}</td>
                  <td className="px-4 py-2 text-center">{r.thru}</td>
                  <td className="px-4 py-2 text-center">{r.gross}</td>
                  <td className="px-4 py-2 text-center">{r.net}</td>
                  {!isMedal && <td className="px-4 py-2 text-center font-bold text-blue-700">{r.points}</td>}
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && results.length === 0 && <div className="text-center py-8 text-gray-500">No scores entered yet.</div>}
        </div>
      </div>
    </Layout>
  );
}
